import React, { useState } from 'react'
import { FlexColumn } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { CaptionSM, CaptionXS, HeadingXS, TitleLG, TitleMD, TitleXS, TitleXXS } from '@/atoms/Text'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TheatricalPromotionContext'
import { FlexibleBookingToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/FlexibleBookingToolTip'
import { SpecificTimesToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/SpecificTimesToolTip'
import { NoFeesValuesType } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/config/NoFeesValues'

const BANNER_CONFIGS = {
  desktop: {
    videoStyles: 'z-[9] right-0',
  },
  tablet: {
    videoStyles: 'z-10 -right-[2em]',
  },
  mobile: {
    videoStyles: 'z-10 -right-[4em]',
  },
}

export const SmallContent = ({ code, bannerValues }: { code: string; bannerValues: NoFeesValuesType }) => {
  const config = BANNER_CONFIGS['desktop']
  const { t } = useTranslate('tickets')
  const [videoFailed, setVideoFailed] = useState(false)
  const { experiments } = useTheatricalPromotionContext()
  const tkokNoFeesExp = experiments ? experiments['tkok-nofees-video'] : undefined
  const handleVideoError = () => {
    setVideoFailed(true)
  }
  return (
    <div className="relative mb-2 rounded-xl bg-[#bce3ea]">
      <div className=" sm relative h-full min-h-[120px] w-full rounded-xl @container/banner @[328px]:min-h-[160px] ">
        <div
          className={
            'relative z-0 hidden h-full min-h-[120px] overflow-hidden rounded-xl @[328px]:block @[328px]:min-h-[160px]  '
          }
        >
          <div className={'absolute left-0 top-0 z-10 h-[120px]   w-full min-w-[200px]  @[328px]:h-[160px]'}>
            <div className={'relative flex w-full justify-end'}>
              <div className="relative right-0 top-0 z-10 block h-[120px] w-[72px] bg-gradient-to-r from-[#bce3ea]  to-transparent to-100% @[328px]:h-[160px] " />
              <div className="w-[270px]" />
            </div>
          </div>
          {tkokNoFeesExp && !videoFailed ? (
            <video
              preload="auto"
              className={`${config.videoStyles} absolute -top-[10%] right-0 h-[110%] w-auto rounded-xl bg-[#cce6ec] object-contain `}
              autoPlay
              loop
              muted
              playsInline
              onError={handleVideoError}
              src={bannerValues.video}
            />
          ) : (
            <Image
              src={bannerValues.image || ''}
              className={`${config.videoStyles} absolute !-right-[1.5em] top-[30%] h-[70%] w-auto rounded-xl object-contain  pr-1 @md:top-0 @md:h-[100%] @lg:-top-[5%] @lg:h-[110%] @xl:!right-0 @4xl:pr-6 `}
              width={200}
              height={200}
              alt={''}
            />
          )}
        </div>
        {/* Main content container - absolute positioning to fill parent */}
        <div className="absolute inset-0 z-10 flex w-full items-stretch rounded-xl  px-1  @[328px]:px-4 ">
          {/*LEFT TEXT*/}
          <FlexColumn className="flex w-full  justify-center py-1 text-center @[328px]:text-left  ">
            <div className={'w-full'}>
              {tkokNoFeesExp ? (
                <>
                  <TitleLG className="relative hidden @[328px]:block" weight="bold" color="black">
                    <Translate i18nKey="theatersaresellingout" t={t}>
                      Theaters Are Selling Out!
                    </Translate>
                  </TitleLG>
                  <TitleMD className=" relative @[328px]:hidden" weight="bold" color="black">
                    <Translate i18nKey="theatersaresellingout" t={t}>
                      Theaters Are Selling Out!
                    </Translate>
                  </TitleMD>
                  <CaptionSM color="core-gray-700" weight={'medium'}>
                    <Translate i18nKey="noonlinefeeswhenbookingtoday" t={t}>
                      No online fees when you book today!
                    </Translate>
                  </CaptionSM>
                </>
              ) : (
                <HeadingXS
                  className=" relative text-xl leading-6 @md:!text-2xl @md:leading-7 "
                  weight="bold"
                  color="black"
                >
                  <Translate i18nKey="noOnlineFeesNoHassle" t={t}>
                    No Online Fees. <span>No Hassle.</span>
                  </Translate>
                </HeadingXS>
              )}

              <FlexColumn className={'mt-1 @[328px]:mt-2'}>
                <div className={'justify-center'}>
                  <TitleXS className=" hidden bg-[#EA3358]  px-3 py-[6px] uppercase text-white @[328px]:inline">
                    {t('useCode', 'Use Code')}:<span className="ml-1 font-bold">{code || 'NOFEES'}</span>
                  </TitleXS>
                  <TitleXXS
                    weight={'medium'}
                    className=" inline bg-[#EA3358]  px-3 py-1 uppercase text-white @[328px]:hidden"
                  >
                    {t('useCode', 'Use Code')}:<span className="ml-1 font-bold">{code || 'NOFEES'}</span>
                  </TitleXXS>
                </div>
              </FlexColumn>
              {/*TOOLTIP*/}
              <div className={'z-[70] mt-0 flex justify-center @[328px]:mt-1 @[328px]:block'}>
                <SpecificTimesToolTip iconSize={12} buttonClassName="!px-0" buttonColor={'black'}>
                  <CaptionXS weight={'medium'}>{t('onSelectShowtimes', 'On Select Showtimes')}</CaptionXS>
                </SpecificTimesToolTip>
              </div>
              <div className={'z-[70] -mt-1 flex justify-center @[328px]:mt-0 @[328px]:block'}>
                <FlexibleBookingToolTip
                  className="mt-1"
                  buttonColor={'black'}
                  buttonClassName="bg-black/10 border-none text-black focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent backdrop-blur-[24px] !backdrop-filter"
                />
              </div>
            </div>
          </FlexColumn>
        </div>
      </div>
    </div>
  )
}
